/**
 * A drawing's tasks, in the two places the drawings screen asks about them
 * (`MCK-20260521-008`, *"the task's output is a drawing"*, owner 2026-08-20).
 *
 * Mounted through `features/drawings/slotTasks.ts`'s seams, composed by the route — this
 * feature already reads `drawings`, and the reverse import would be a cycle.
 *
 * **`SlotTaskList` is the coordination view**: what the drawing still owes, and what its
 * versions resolved. Two requests whatever the drawing's history — `GET /slot-tasks` for the
 * first half and the register's own list, `under=<slot>&resolved=true`, for the second. The
 * second is exact rather than approximate because the server refuses to record a resolving
 * version from any drawing but the task's own (`service.py:_link_resolving_version`).
 *
 * **`PublishedTaskCloser` is the publish flow's proposal.** It runs after the version exists,
 * because closing a task *by* a version needs the version's id, and it closes nothing a person
 * did not tick — *"this publish happens to be on the same slot"* is not evidence.
 */

import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button, InlineError, SectionTitle, SkeletonRows } from "@/components";
import type { PublishedTasksProps, SlotTaskListProps } from "@/features/drawings";
import type { MessageKeys } from "@/lib/i18n";
import { useMessages } from "@/lib/i18n";

import type { Task, TaskStatus } from "./api";
import { NO_FILTERS } from "./filters";
import { detailOf, taskErrorKey } from "./problems";
import type { ResolveFailure } from "./queries";
import { useCloseTasksByVersion, useSlotTasks, useTaskList } from "./queries";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

/** TWIN: the same table in `TaskHierarchy`, `TaskFilterBar` and `TaskDetail`. */
const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "status.open",
  in_progress: "status.inProgress",
  done: "status.done",
  closed: "status.closed",
};

/** `#12 Title`, linking to C8 — the register row's own link, at the size of a list line. */
function TaskLink({ task }: { readonly task: Task }) {
  const t = useMessages("tasks");
  return (
    <Link
      to="/projects/$projectId/tasks/$taskId"
      params={{ projectId: task.project_id, taskId: task.id }}
      className={styles.rowLink}
    >
      <span className={styles.number}>{t("row.number", { number: task.number })}</span>{" "}
      {task.title}
    </Link>
  );
}

export function SlotTaskList({ projectId, slotId }: SlotTaskListProps) {
  const t = useMessages("tasks");
  const owed = useSlotTasks(projectId, slotId);
  // The register's own query, narrowed to this drawing and to *closed by a version*. The
  // whole filter object is the key (`taskListQueryOptions`), so a register already showing the
  // same narrowing shares the cache entry rather than asking twice.
  const resolvedFilters = useMemo(
    () => ({ ...NO_FILTERS, under: [slotId], resolved: true }),
    [slotId],
  );
  const resolved = useTaskList(projectId, resolvedFilters, "register");
  const firstPage = resolved.data?.pages[0];

  return (
    <section className={styles.slotTasks}>
      <SectionTitle as="h3">{t("slot.title")}</SectionTitle>

      <h4 className={styles.slotTasksHeading}>{t("slot.open")}</h4>
      {owed.isPending ? (
        <SkeletonRows label={t("slot.loading")} rows={2} columns={["70%"]} rowHeight={24} />
      ) : owed.isError ? (
        <InlineError variant="block">{t("slot.error")}</InlineError>
      ) : owed.data.length === 0 ? (
        <p className={styles.muted}>{t("slot.noneOpen")}</p>
      ) : (
        <ul className={styles.slotTaskList}>
          {owed.data.map((task) => (
            <li key={task.id} className={styles.slotTaskRow}>
              <TaskLink task={task} />
              <span className={styles.muted}>{t(STATUS_KEY[task.status])}</span>
            </li>
          ))}
        </ul>
      )}

      <h4 className={styles.slotTasksHeading}>{t("slot.resolved")}</h4>
      {resolved.isPending ? (
        <SkeletonRows label={t("slot.loading")} rows={2} columns={["70%"]} rowHeight={24} />
      ) : resolved.isError ? (
        <InlineError variant="block">{t("slot.error")}</InlineError>
      ) : firstPage === undefined || firstPage.items.length === 0 ? (
        <p className={styles.muted}>{t("slot.noneResolved")}</p>
      ) : (
        <>
          <ul className={styles.slotTaskList}>
            {firstPage.items.map((task) => (
              <li key={task.id} className={styles.slotTaskRow}>
                <TaskLink task={task} />
              </li>
            ))}
          </ul>
          {/* One page is what a drawing's detail pane shows. More than a page of tasks closed
              by one drawing's versions is a register question, and the register answers it. */}
          {firstPage.has_more ? <p className={styles.muted}>{t("slot.more")}</p> : null}
        </>
      )}
    </section>
  );
}

/** Whether this caller can walk the task to `closed` — directly, or through `done` first. */
function closable(task: Task): boolean {
  return task.allowed_transitions.includes("closed") || task.allowed_transitions.includes("done");
}

export function PublishedTaskCloser({
  projectId,
  slotId,
  versionId,
  onDone,
}: PublishedTasksProps) {
  const t = useMessages("tasks");
  const owed = useSlotTasks(projectId, slotId);
  const close = useCloseTasksByVersion(projectId);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const [failures, setFailures] = useState<readonly ResolveFailure[] | null>(null);

  // Called once. The drawer passes an inline callback, and an effect that re-ran on a fresh one
  // could close the drawer twice.
  const finished = useRef(false);
  const finish = useRef(onDone);
  useEffect(() => {
    finish.current = onDone;
  }, [onDone]);
  const done = (): void => {
    if (finished.current) return;
    finished.current = true;
    finish.current();
  };

  const rows = owed.data ?? [];
  const offered = rows.filter(closable);
  // Offered rows only: a task this caller may not move is not a choice, and a box that could
  // only earn a `403` is a box nobody should be handed.
  const notYours = rows.length - offered.length;

  // Nothing to propose is not a step. A drawing that owes this caller nothing closes the drawer
  // exactly as publishing did before this existed.
  const nothingToOffer = owed.isSuccess && offered.length === 0 && failures === null;
  useEffect(() => {
    if (nothingToOffer) done();
  });

  if (owed.isPending || nothingToOffer) {
    return <SkeletonRows label={t("slot.loading")} rows={3} columns={["70%"]} rowHeight={28} />;
  }

  const chosen = offered.filter((task) => ticked.has(task.id));

  return (
    <div className={styles.resolveStep}>
      <SectionTitle as="h3">{t("resolve.title")}</SectionTitle>
      <p className={styles.muted}>{t("resolve.body")}</p>

      {owed.isError ? (
        <InlineError variant="block">{t("slot.error")}</InlineError>
      ) : (
        <ul className={styles.slotTaskList}>
          {offered.map((task) => (
            <li key={task.id} className={styles.slotTaskRow}>
              <label className={styles.resolveOption}>
                <input
                  type="checkbox"
                  checked={ticked.has(task.id)}
                  disabled={close.isPending}
                  onChange={(event) => {
                    const on = event.currentTarget.checked;
                    setTicked((current) => {
                      const next = new Set(current);
                      if (on) next.add(task.id);
                      else next.delete(task.id);
                      return next;
                    });
                  }}
                />
                <span className={styles.number}>
                  {t("row.number", { number: task.number })}
                </span>
                <span className={styles.resolveTaskTitle}>{task.title}</span>
                <span className={styles.muted}>{t(STATUS_KEY[task.status])}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {notYours > 0 ? (
        <p className={styles.muted}>{t("resolve.notYours", { count: notYours })}</p>
      ) : null}

      {/* What did not close, each in the server's own sentence — the verification gate names
          the person who has to close it, which is the one thing worth reading here. A task that
          reached `done` and stopped there stays `done`: the sheet it waited on is published. */}
      {failures === null || failures.length === 0 ? null : (
        <div className={styles.notice} role="alert">
          <p className={styles.noticeText}>{t("resolve.failed")}</p>
          <ul className={styles.slotTaskList}>
            {failures.map(({ task, error }) => (
              <li key={task.id} className={styles.noticeText}>
                <span className={styles.number}>{t("row.number", { number: task.number })}</span>{" "}
                {task.title}
                {" — "}
                {detailOf(error) ?? t(taskErrorKey(error))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.resolveActions}>
        <Button
          variant="primary"
          loading={close.isPending}
          disabled={chosen.length === 0}
          onClick={() => {
            setFailures(null);
            close.mutate(
              { versionId, tasks: chosen },
              {
                onSuccess: (result) => {
                  if (result.length === 0) {
                    done();
                    return;
                  }
                  setFailures(result);
                  setTicked(new Set());
                },
              },
            );
          }}
        >
          {t("resolve.close", { count: chosen.length })}
        </Button>
        <Button variant="tertiary" disabled={close.isPending} onClick={done}>
          {failures === null ? t("resolve.skip") : t("resolve.done")}
        </Button>
      </div>
    </div>
  );
}
